import React from "react";

import "../style/reset.css"
import "../style/typography.css"

import {
  Capital,
  FullWidthContainer,
  GlobalStyle,
  Heading2,
  Heading4, InternalLink, MaxWidthContainer
} from "../style/itf-styled-components";
import {ThemeProvider} from "styled-components";
import {ExternalLink} from "../components/external-link";
import {useMemberProfileImages} from "../data/hooks/member-profile-images";
import {useThemes} from "../style/themes.hook";
import SEO from "../components/seo";
import {useGallery} from "../data/hooks/gallery";
import {Description} from "../components/description";
import {graphql, useStaticQuery} from "gatsby";
import Header from "../components/header/header";
import Img from "gatsby-image/withIEPolyfill";
import LightboxGallery from "../components/lightbox-gallery";
import Photo from "../components/photo";
import ItemsGrid from "../components/items-grid";
import Footer from "../components/footer";
import {CookiesProvider} from "react-cookie";
import CookiesConsent from "../components/cookies-consent";
import {useWorkshops} from "../data/hooks/workshops";

function shuffle(a) {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const SpeakerProfile = ({pageContext}) => {
  const themes = useThemes();
  const speaker = pageContext.speaker;
  const photos = useGallery(speaker.slug);
  const profileImage = useMemberProfileImages().find(i => i.name === speaker.slug);
  const workshops = useWorkshops();
  const defaultFeaturedImage = useStaticQuery(graphql`
    query {
      file(relativePath: { eq: "lens-shutter.jpg" }) {
        childImageSharp {
          fluid {
            ...GatsbyImageSharpFluid_tracedSVG
          }
        }
      }
    }
  `).file;

  return (
    <>
      <SEO
        title={speaker.name}
        description={speaker.seoDescription}
      />
      <ThemeProvider theme={themes.light}>
        <GlobalStyle/>
        <FullWidthContainer
          css={`
              background-color: ${props => props.theme.background()};
              color: ${props => props.theme.text};
              
              @media (max-width: ${props => props.theme.mobileWidth}) {
                padding-top: 45px;
              }
            `}
        >
          <Header page={"speakers-and-trainers"}/>
          <MaxWidthContainer
            css={`
              padding-top: 55px;
              @media (max-width: ${props => props.theme.mobileWidth}) {
                padding: 0;
              }
            `}
          >
            <div
              css={`
                display: grid;
                grid-template-columns: 3fr 9fr;
              
                @media (max-width: 768px) {
                  display: flex;
                  flex-direction: column;
                }
              `}
            >
              <div
                css={`
                  display: flex;
                  flex-direction: column;
                  padding: 0 15px 25px;

                  @media (max-width: ${props => props.theme.mobileWidth}) {
                    padding: 0;
                  }
                `}
              >
                <Img
                  css={`
                    border-radius: 23px;
                    
                    @media (max-width: ${props => props.theme.mobileWidth}) {
                      border-radius: 0;
                    }
                  `}
                  sizes={{
                    ...(profileImage || defaultFeaturedImage).childImageSharp.fluid,
                    aspectRatio: 1
                  }}
                  objectPosition="50% top"
                />
                <div
                  css={`
                    display: flex;
                    flex-direction: column;
                    font-weight: 700;
                    
                    @media (max-width: ${props => props.theme.mobileWidth}) {
                      padding: 0 25px;
                    }
                  `}
                >
                  {!!speaker.roles.filter(role => role.type === "speaker").length && <Heading4>Speaker ITF</Heading4>}
                  {speaker.roles
                    .filter(role => role.type === 'speaker')
                    .map(role => role.events.map(event => (
                      <InternalLink
                        to={`/agenda-conferinta-${role.category}imagine${!!event ? `#${event}` : ''}`}
                        key={`${role.type}_${event}`}
                      >Conferința <Capital>{role.category}</Capital>Imagine</InternalLink>
                      ))
                    )
                  }

                  {!!speaker.roles.filter(role => role.type === "trainer").length && <Heading4>Trainer ITF</Heading4>}
                  {speaker.roles
                    .filter(role => role.type === 'trainer')
                    .map(role => role.events.map(event_slug =>
                      (<InternalLink
                          to={`/workshopuri#${event_slug}`}
                          key={`${role.type}_${event_slug}`}
                        >{
                          `${{
                              masterclass_product_photography: `Masterclass - Fotografie de produs`,
                              masterclass_landscape_photography: `Masterclass - Fotografie de peisaj`,
                              masterclass_newborn_photography: `Masterclass - Fotografie newborn`,
                              photo_tour_mobile_photography: `Tura foto - Fotografie mobile`,
                              one_day_workshop_glam_fashion_photography: `Workshop de o zi - Fotografie glam/fashion`,
                            }[(workshops
                              .find(workshop => workshop.slug === event_slug) || {})
                            .type] || 'Masterclass'} - `}<Capital>{role.category}</Capital>Imagine</InternalLink>)
                    ))}
                  {!!speaker.roles.filter(role => role.type === "organizer").length && <Heading4>Organizator ITF</Heading4>}
                </div>
                <div
                  css={`
                    @media (max-width: ${props => props.theme.mobileWidth}) {
                      padding: 0 25px;
                    }
                  `}
                >
                  {!!speaker.links && <Heading4>Contact:</Heading4>}
                  {(speaker.links || []).map((link, index) => (
                    <ExternalLink
                      css={`
                        display: block;
                        word-break: break-all;
                        padding: 5px 0;
                      `}
                      key={index}
                      to={link}
                    >{link}</ExternalLink>
                  ))}
                </div>
              </div>
              <div
                css={`
                margin-bottom: 20px;
                
                @media (max-width: ${props => props.theme.mobileWidth}) {
                  padding: 0 25px;
                }
              `}
              >
                <div>
                  <div
                    css={`
                    padding: 0 210px 50px 0;
  
                    @media (max-width: ${props => props.theme.mobileWidth}) {
                      padding: 0;
                    }
                  `}
                  >
                    <Heading2>{speaker.name}</Heading2>
                    <>
                      {(speaker.description || [{
                        type: 'paragraph',
                        text: 'Nu există descriere'
                      }]).map((i, index) => (
                        <Description
                          key={`${speaker.name}-${index}`}
                          type={i.type}
                          value={i.value}
                        />
                      ))}
                    </>
                  </div>
                </div>
                {!!photos.length && (
                  <LightboxGallery
                    items={shuffle(photos).map((photo, _) => <Photo photo={photo}/>)}
                    layout={<ItemsGrid/>}
                  />
                )}
              </div>
            </div>
          </MaxWidthContainer>
          <Footer/>

          <CookiesProvider>
            <CookiesConsent/>
          </CookiesProvider>
        </FullWidthContainer>
      </ThemeProvider>
    </>
  )
    ;
};

export default SpeakerProfile;